import { CheckCircle2, AlertTriangle, MinusCircle } from "lucide-react";
import { Card } from "@/components/ui/primitives";
import type { ExtractedFieldDTO } from "@/lib/api";

export function ExtractedDataTable({ fields }: { fields: ExtractedFieldDTO[] }) {
  if (fields.length === 0) return null;
  return (
    <Card className="overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-5 py-3 text-left font-medium">Field</th>
            <th className="px-5 py-3 text-left font-medium">Value</th>
            <th className="px-5 py-3 text-left font-medium">Confidence</th>
            <th className="px-5 py-3 text-left font-medium">Source</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f, i) => (
            <tr key={f.id} className={i === 0 ? "" : "border-t border-border"}>
              <td className="px-5 py-3 text-foreground/80">{f.label}</td>
              <td className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{f.value}</span>
                  <CrossCheckIcon status={f.cross_check_status} />
                </div>
              </td>
              <td className="px-5 py-3">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{
                      backgroundColor: `hsl(${Math.round(f.confidence * 140)} 70% 45%)`,
                    }}
                  />
                  <span className="font-medium tabular-nums">{Math.round(f.confidence * 100)}%</span>
                </div>
              </td>
              <td className="px-5 py-3 text-muted-foreground">
                {f.source_filename ? (
                  <>
                    {f.source_filename}
                    {f.source_page ? <span className="ml-1 text-xs">p.{f.source_page}</span> : null}
                  </>
                ) : (
                  <span className="text-xs italic">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function CrossCheckIcon({ status }: { status?: string | null }) {
  if (!status) return null;
  if (status === "match")
    return <CheckCircle2 className="h-4 w-4 text-success" strokeWidth={2.1} aria-label="Cross-check: match" />;
  if (status === "mismatch")
    return <AlertTriangle className="h-4 w-4 text-warn" strokeWidth={2.1} aria-label="Cross-check: mismatch" />;
  if (status === "not_applicable") return <MinusCircle className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />;
  return null;
}
